import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function PhotoCompareSlider({ 
  beforeUrl, 
  afterUrl, 
  beforeLabel = 'Przed',
  afterLabel = 'Po',
  onClose 
}) {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
      {/* Close Button */}
      <Button 
        variant="ghost" 
        size="icon"
        className="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
        onClick={onClose}
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Compare Container */}
      <div 
        ref={containerRef}
        className="relative max-w-4xl w-full max-h-[80vh] aspect-video cursor-ew-resize select-none overflow-hidden rounded-sm"
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        {/* After Image (Full width, behind) */}
        <img 
          src={afterUrl} 
          alt="Po"
          className="absolute inset-0 w-full h-full object-contain"
          draggable={false}
        />
        
        {/* Before Image (Clipped) */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <img 
            src={beforeUrl} 
            alt="Przed"
            className="absolute inset-0 w-full h-full object-contain"
            style={{ 
              width: `${100 * (100 / sliderPosition)}%`,
              maxWidth: 'none'
            }}
            draggable={false}
          />
        </div>

        {/* Slider Line */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-lg"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          onMouseDown={() => setIsDragging(true)}
          onTouchStart={() => setIsDragging(true)}
        >
          {/* Slider Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
            <ChevronLeft className="w-4 h-4 text-black -mr-1" />
            <ChevronRight className="w-4 h-4 text-black -ml-1" />
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 rounded-sm text-white text-sm">
          {beforeLabel}
        </div>
        <div className="absolute top-4 right-4 px-3 py-1 bg-black/70 rounded-sm text-white text-sm">
          {afterLabel}
        </div>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-8 text-white/60 text-sm">
        Przeciągnij suwak, aby porównać zdjęcia
      </div>
    </div>
  );
}
